import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';

const Category = ({ products = [], crystalTypes = [], categories = [] }) => {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    crystalTypeId: searchParams.get('crystal') || '',
    categoryId: categoryId || searchParams.get('category') || '',
    sortBy: searchParams.get('sort') || 'popularity',
    priceRange: searchParams.get('price') || ''
  });

  // 当URL参数或路由参数变化时更新过滤条件
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      crystalTypeId: searchParams.get('crystal') || '',
      categoryId: categoryId || searchParams.get('category') || '',
      sortBy: searchParams.get('sort') || prev.sortBy,
      priceRange: searchParams.get('price') || prev.priceRange
    }));
  }, [categoryId, searchParams]);

  // 处理过滤条件变化
  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    
    // 更新URL参数
    const params = new URLSearchParams();
    if (newFilters.crystalTypeId) params.set('crystal', newFilters.crystalTypeId);
    if (newFilters.categoryId && !categoryId) params.set('category', newFilters.categoryId);
    if (newFilters.sortBy !== 'popularity') params.set('sort', newFilters.sortBy);
    if (newFilters.priceRange) params.set('price', newFilters.priceRange);
    
    setSearchParams(params);
  };

  // 过滤和排序商品 - 添加数据验证
  const filteredProducts = (products && Array.isArray(products) ? products : []).filter(product => {
    // 水晶类型过滤
    if (filters.crystalTypeId && product.crystalTypeId !== parseInt(filters.crystalTypeId)) {
      return false;
    }
    
    // 首饰类别过滤
    if (filters.categoryId && product.categoryId !== parseInt(filters.categoryId)) {
      return false;
    }
    
    // 价格范围过滤
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      if (min && product.price < min) return false;
      if (max && product.price > max) return false;
    }
    
    return true;
  }).sort((a, b) => {
    // 排序
    switch (filters.sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'popularity':
      default:
        return b.popularity - a.popularity;
    }
  });

  // 获取当前分类或水晶类型名称
  const getCurrentTitle = () => {
    if (filters.crystalTypeId) {
      const crystalType = crystalTypes.find(type => type.id === parseInt(filters.crystalTypeId));
      return crystalType ? crystalType.name : '全部水晶';
    }
    
    if (filters.categoryId) {
      const category = categories.find(cat => cat.id === parseInt(filters.categoryId));
      return category ? category.name : '全部分类';
    }
    
    return '全部商品';
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 text-center">{getCurrentTitle()}</h1>
      
      {/* 筛选区域 */}
      <div className="bg-white bg-opacity-80 rounded-lg p-4 mb-6 shadow-md">
        <div className="flex flex-wrap gap-4">
          {/* 水晶类型筛选 */}
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">水晶类型</label>
            <select
              value={filters.crystalTypeId}
              onChange={(e) => handleFilterChange('crystalTypeId', e.target.value)}
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">全部水晶</option>
              {crystalTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          
          {/* 首饰类别筛选 */}
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">首饰类别</label>
            <select
              value={filters.categoryId}
              onChange={(e) => handleFilterChange('categoryId', e.target.value)}
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">全部类别</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          
          {/* 价格范围筛选 */}
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">价格范围</label>
            <select
              value={filters.priceRange}
              onChange={(e) => handleFilterChange('priceRange', e.target.value)}
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">全部价格</option>
              <option value="0-199">¥0 - ¥199</option>
              <option value="200-299">¥200 - ¥299</option>
              <option value="300-499">¥300 - ¥499</option>
              <option value="500-999">¥500 - ¥999</option>
              <option value="1000-">¥1000以上</option>
            </select>
          </div>
          
          {/* 排序方式 */}
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">排序方式</label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="popularity">按热度</option>
              <option value="price-asc">价格从低到高</option>
              <option value="price-desc">价格从高到低</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* 商品列表 */}
      {filteredProducts.length > 0 ? (
        <ProductGrid products={filteredProducts} />
      ) : (
        <div className="text-center py-10 bg-white bg-opacity-80 rounded-lg shadow-md">
          <p className="text-gray-500">没有找到符合条件的商品</p>
          <button
            onClick={() => {
              setFilters({
                crystalTypeId: '',
                categoryId: '',
                sortBy: 'popularity',
                priceRange: ''
              });
              setSearchParams({});
            }}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            清除筛选条件
          </button>
        </div>
      )}
    </div>
  );
};

export default Category;